import { BillingBreakdownLine } from '../components/BillingBreakdownDisplay';

export interface AppendixRateRange {
  from: number;
  to: number | null;
  rate: number;
}

export interface BillingPreview {
  lines: BillingBreakdownLine[];
  total_amount: number;
}

function parseRangeTo(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function normalizeRanges(ranges: Array<{ from?: number | string; to?: number | string | null; rate?: number | string }>): AppendixRateRange[] {
  return ranges
    .filter((r) => r && Number(r.rate) >= 0)
    .map((r) => ({
      from: Number(r.from ?? 1),
      to: parseRangeTo(r.to),
      rate: Number(r.rate ?? 0),
    }))
    .sort((a, b) => a.from - b.from);
}

function getAppendixRateRanges(custom: Record<string, unknown>): AppendixRateRange[] {
  const ranges = custom.appendix_c_rate_ranges;
  if (Array.isArray(ranges) && ranges.length > 0) {
    return normalizeRanges(ranges);
  }
  const singleRate = Number(custom.appendix_c_rate ?? 0);
  if (singleRate > 0) {
    return [{ from: 1, to: null, rate: singleRate }];
  }
  return [];
}

function applyTieredRanges(totalPages: number, ranges: AppendixRateRange[]): { lines: BillingBreakdownLine[]; total: number } {
  const lines: BillingBreakdownLine[] = [];
  let total = 0;
  let pagesAccountedFor = 0;

  for (const range of ranges) {
    if (pagesAccountedFor >= totalPages) break;

    const from = Math.max(1, range.from);
    const to = range.to;
    const rate = range.rate;

    const startPage = Math.max(pagesAccountedFor + 1, from);
    const endPage = to !== null ? Math.min(totalPages, to) : totalPages;
    const quantity = Math.max(0, endPage - startPage + 1);

    if (quantity <= 0) continue;

    const amount = Math.round(quantity * rate * 100) / 100;
    const label = to !== null ? `Appendix pages (${from}–${to})` : `Appendix pages (${from}+)`;

    lines.push({ label, quantity, rate, amount });
    total += amount;
    pagesAccountedFor += quantity;
  }

  return { lines, total: Math.round(total * 100) / 100 };
}

/**
 * Must match ClientBillingCalculationService::rushRateApplies on the backend.
 */
export function rushRateApplies(custom: Record<string, unknown> | undefined, isRush: boolean): boolean {
  return isRush && Number(custom?.rush_rate ?? 0) > 0;
}

export function calculatePageBasedPreview(
  custom: Record<string, unknown>,
  totalPages: number,
  reviewedPages: number,
  appendixIncluded: boolean,
  defaultPageRate?: number,
  isRush = false
): BillingPreview | null {
  if (!totalPages || !reviewedPages || reviewedPages > totalPages) return null;

  const rushApplied = rushRateApplies(custom, isRush);
  const pageRate = rushApplied ? Number(custom.rush_rate) : Number(defaultPageRate ?? custom.rate ?? 0);
  const lines: BillingBreakdownLine[] = [];

  const reviewedAmount = Math.round(reviewedPages * pageRate * 100) / 100;
  lines.push({
    label: rushApplied ? 'Reviewed pages (RUSH rate)' : 'Reviewed pages',
    quantity: reviewedPages,
    rate: pageRate,
    amount: reviewedAmount,
  });

  let totalAmount = reviewedAmount;
  const appendixPages = Math.max(0, totalPages);

  if (appendixIncluded && appendixPages > 0) {
    const tierResult = applyTieredRanges(appendixPages, getAppendixRateRanges(custom));
    lines.push(...tierResult.lines);
    totalAmount += tierResult.total;
  }

  return {
    lines,
    total_amount: Math.round(totalAmount * 100) / 100,
  };
}

export function calculateConsolidationPreview(
  custom: Record<string, unknown>,
  totalPages: number,
  appendixIncluded: boolean,
  defaultPageRate?: number
): BillingPreview | null {
  if (!totalPages) return null;

  const pageRate = Number(defaultPageRate ?? custom.rate ?? 0);
  const appendixRate = Number(custom.appendix_c_rate ?? 0);
  const lines: BillingBreakdownLine[] = [];

  const mainAmount = Math.round(totalPages * pageRate * 100) / 100;
  lines.push({
    label: 'Consolidation pages',
    quantity: totalPages,
    rate: pageRate,
    amount: mainAmount,
  });

  let totalAmount = mainAmount;

  if (appendixIncluded && appendixRate > 0) {
    const appendixAmount = Math.round(totalPages * appendixRate * 100) / 100;
    lines.push({
      label: 'Appendix C',
      quantity: totalPages,
      rate: appendixRate,
      amount: appendixAmount,
    });
    totalAmount += appendixAmount;
  }

  return {
    lines,
    total_amount: Math.round(totalAmount * 100) / 100,
  };
}

export function applyDiscountToPreview(
  preview: BillingPreview | null,
  discountAmount: number,
  reason?: string
): BillingPreview | null {
  if (!preview) return null;
  const discount = Math.max(0, Math.min(Number(discountAmount) || 0, preview.total_amount));
  if (discount <= 0) return preview;
  const lines = [
    ...preview.lines,
    {
      label: reason ? `Discount (${reason})` : 'Discount',
      quantity: 1,
      rate: -discount,
      amount: -discount,
    },
  ];
  return {
    lines,
    total_amount: Math.round((preview.total_amount - discount) * 100) / 100,
  };
}

export interface InvoiceStatementLine {
  label: string;
  quantity: number | null;
  rate: number | null;
  amount: number | null;
}

interface StatementEstimate {
  total_pages?: number | null;
  reviewed_pages?: number | null;
  rate?: number | string | null;
  appendix_included?: boolean | number | null;
  appendix_c_quantity?: number | null;
  appendix_c_rate?: number | string | null;
  discount_amount?: number | string | null;
  discount_reason?: string | null;
  billing_breakdown?: {
    lines?: Array<{
      label: string;
      quantity: number;
      rate: number;
      amount: number;
    }>;
  } | null;
}

function appendixStatementLabel(label: string): string {
  const match = label.match(/\(([^)]+)\)/);
  return match ? `Appendix C (${match[1]})` : 'Appendix C';
}

/**
 * Not reviewed is the non-significant page count: actual pages minus medical pages reviewed.
 */
export function invoiceStatementLines(estimate: StatementEstimate | null | undefined): InvoiceStatementLine[] {
  if (estimate?.total_pages == null) {
    return [];
  }

  const totalPages = Number(estimate.total_pages);
  if (!Number.isFinite(totalPages)) {
    return [];
  }

  const breakdown = estimate.billing_breakdown?.lines ?? [];
  const reviewedLine = breakdown.find((line) => line.label.toLowerCase().includes('reviewed'));
  const appendixLines = breakdown.filter((line) => line.label.toLowerCase().includes('appendix'));
  const discountLine = breakdown.find((line) => line.label.toLowerCase().includes('discount'));
  const otherChargeLines = breakdown.filter((line) => {
    const label = line.label.toLowerCase();
    return !label.includes('reviewed') && !label.includes('appendix') && !label.includes('discount');
  });

  const reviewedPages = estimate.reviewed_pages != null
    ? Number(estimate.reviewed_pages)
    : reviewedLine
      ? Number(reviewedLine.quantity)
      : null;

  const lines: InvoiceStatementLine[] = [{
    label: 'Actual pages',
    quantity: totalPages,
    rate: null,
    amount: null,
  }];

  if (reviewedPages != null && Number.isFinite(reviewedPages)) {
    const rate = reviewedLine
      ? Number(reviewedLine.rate)
      : estimate.rate != null
        ? Number(estimate.rate)
        : null;
    const amount = reviewedLine
      ? Number(reviewedLine.amount)
      : rate != null
        ? Math.round(reviewedPages * rate * 100) / 100
        : null;

    lines.push({
      label: 'Medical pages reviewed',
      quantity: reviewedPages,
      rate,
      amount,
    });
    lines.push({
      label: 'Not reviewed',
      quantity: Math.max(0, totalPages - reviewedPages),
      rate: null,
      amount: 0,
    });
  }

  otherChargeLines.forEach((line) => {
    lines.push({
      label: line.label,
      quantity: Number(line.quantity),
      rate: Number(line.rate),
      amount: Number(line.amount),
    });
  });

  if (appendixLines.length > 0) {
    appendixLines.forEach((line) => {
      lines.push({
        label: appendixStatementLabel(line.label),
        quantity: Number(line.quantity),
        rate: Number(line.rate),
        amount: Number(line.amount),
      });
    });
  } else if (estimate.appendix_included && Number(estimate.appendix_c_quantity) > 0) {
    const quantity = Number(estimate.appendix_c_quantity);
    const rate = estimate.appendix_c_rate != null && estimate.appendix_c_rate !== ''
      ? Number(estimate.appendix_c_rate)
      : null;
    lines.push({
      label: 'Appendix C',
      quantity,
      rate,
      amount: rate != null ? Math.round(quantity * rate * 100) / 100 : null,
    });
  }

  if (discountLine) {
    lines.push({
      label: discountLine.label || 'Discount',
      quantity: null,
      rate: null,
      amount: Number(discountLine.amount),
    });
  } else {
    const discount = Math.round((Number(estimate.discount_amount) || 0) * 100) / 100;
    if (discount > 0) {
      lines.push({
        label: estimate.discount_reason ? `Discount (${estimate.discount_reason})` : 'Discount',
        quantity: null,
        rate: null,
        amount: -discount,
      });
    }
  }

  return lines;
}

export function formatStatementDescription(line: InvoiceStatementLine): string {
  if (line.quantity == null) {
    return line.label;
  }
  return `${line.label}: ${line.quantity.toLocaleString('en-US')}`;
}

export function calculateBillingPreview(
  billingType: string | undefined,
  custom: Record<string, unknown> | undefined,
  formData: {
    total_pages: string;
    reviewed_pages: string;
    appendix_included: boolean;
  },
  defaultPageRate?: number,
  isRush = false
): BillingPreview | null {
  if (!billingType || !custom) return null;

  const totalPages = parseInt(formData.total_pages, 10);
  const reviewedPages = parseInt(formData.reviewed_pages, 10);

  if (billingType === 'Page Based') {
    return calculatePageBasedPreview(custom, totalPages, reviewedPages, formData.appendix_included, defaultPageRate, isRush);
  }
  if (billingType === 'Consolidation') {
    return calculateConsolidationPreview(custom, totalPages, formData.appendix_included, defaultPageRate);
  }
  return null;
}
